/**
 * ReportsPage.js — PeoplesDock Reports & Analytics
 *
 * ADDED IN THIS VERSION:
 *   NEW TAB: "Salary Register" (key: 'salary_register')
 *
 *   SalaryRegisterTab component:
 *   - Month + Year selector (shown when this tab is active)
 *   - Fetches GET /reports/salary-register?year=&month=
 *   - Displays full employee payroll table:
 *       Emp Code | Name | Dept | Designation | Basic | HRA | Special | Gross
 *       | PF | ESI | TDS | PT | Loan | Other Ded | Total Ded | Net Pay | Status
 *   - TOTALS row at bottom (highlighted in indigo)
 *   - Department-wise summary section below the main table
 *   - Download Excel button — generates a formatted .xlsx using SheetJS
 *       (SheetJS / xlsx is already available in PeoplesDock frontend dependencies)
 *   - Two sheets in the Excel: "Salary Register" + "Department Summary"
 *   - Company name + month/year in Excel header row
 *   - All amounts formatted as Indian currency (₹ with comma separation)
 *   - Empty state: friendly message when no payroll data for selected month
 *
 * All 10 existing tabs are 100% identical to the deployed version.
 * The only additions are: SalaryRegisterTab component, 'salary_register' tab
 * entry in TABS array, month selector condition, and tab render line.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, Row, Col, Button, Badge, Spinner,
    Form, Alert, Table, Nav
} from 'react-bootstrap';
import {
    BarChart2, Users, TrendingDown, Building2, Calendar,
    Clock, CreditCard, FileText, Target, Download,
    AlertCircle, ArrowUp, ArrowDown, ChevronRight,
    UserCheck, UserMinus, IndianRupee,
} from 'lucide-react';
import {
    BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, FunnelChart, Funnel, LabelList,
    AreaChart, Area,
} from 'recharts';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';

// ── Constants ────────────────────────────────────────────────────────────────
const MONTHS  = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTHS_FULL = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const COLOURS = ['#4f46e5','#7c3aed','#059669','#d97706','#dc2626','#0891b2',
                 '#84cc16','#f43f5e','#6366f1','#10b981','#f59e0b','#8b5cf6'];
const CURR_YEAR  = new Date().getFullYear();
const CURR_MONTH = new Date().getMonth() + 1;

const fmt  = (n) => n?.toLocaleString('en-IN') || '0';
const fmtC = (n) => n ? `₹${parseFloat(n).toLocaleString('en-IN',{ maximumFractionDigits: 0 })}` : '₹0';
const fmtH = (n) => parseFloat(n || 0).toFixed(1) + 'h';
const mLabel = (m) => MONTHS[(parseInt(m) || 1) - 1];

// ── KPI card ─────────────────────────────────────────────────────────────────
const KPICard = ({ label, value, sub, color = '#4f46e5', icon: Icon, trend, trendLabel }) => (
    <Card className="border-0 shadow-sm h-100" style={{ borderLeft: `4px solid ${color}` }}>
        <Card.Body className="p-3">
            <div className="d-flex justify-content-between align-items-start mb-2">
                <div className="text-muted small">{label}</div>
                {Icon && <Icon size={18} style={{ color }} className="opacity-75"/>}
            </div>
            <div style={{ fontSize: 26, fontWeight: 700, color, lineHeight: 1.1 }}>{value}</div>
            {sub && <div className="text-muted" style={{ fontSize: 12, marginTop: 2 }}>{sub}</div>}
            {trend !== undefined && (
                <div className="mt-1" style={{ fontSize: 12 }}>
                    {trend > 0
                        ? <span className="text-success"><ArrowUp size={11}/> {trend}% {trendLabel}</span>
                        : trend < 0
                        ? <span className="text-danger"><ArrowDown size={11}/> {Math.abs(trend)}% {trendLabel}</span>
                        : <span className="text-muted">—</span>}
                </div>
            )}
        </Card.Body>
    </Card>
);

const SectionHeader = ({ title, subtitle }) => (
    <div className="mb-3">
        <h6 className="fw-bold mb-0">{title}</h6>
        {subtitle && <p className="text-muted small mb-0">{subtitle}</p>}
    </div>
);

const ExportBtn = ({ type, year, month, label }) => {
    const [loading, setLoading] = useState(false);
    const download = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ year, ...(month ? { month } : {}) });
            const res = await axiosInstance.get(`/reports/export/${type}?${params}`, { responseType: 'blob' });
            const url  = URL.createObjectURL(res.data);
            const a    = document.createElement('a');
            a.href     = url;
            a.download = `${type}_${year}${month ? '_'+String(month).padStart(2,'0') : ''}.csv`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success('Export ready');
        } catch { toast.error('Export failed'); }
        finally { setLoading(false); }
    };
    return (
        <Button variant="outline-secondary" size="sm" onClick={download} disabled={loading}>
            {loading ? <Spinner size="sm" className="me-1"/> : <Download size={14} className="me-1"/>}
            {label || 'Export CSV'}
        </Button>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ════════════════════════════════════════════════════════════════════════════
const ReportsPage = () => {
    const { user }      = useAuth();
    const [tab, setTab] = useState('overview');
    const [year, setYear]   = useState(CURR_YEAR);
    const [month, setMonth] = useState(CURR_MONTH);

    // Show month selector for these tabs
    const MONTH_TABS = ['attendance', 'payroll', 'timesheet', 'salary_register'];

    const TABS = [
        { key: 'overview',         label: 'Overview',         icon: BarChart2      },
        { key: 'headcount',        label: 'Headcount',        icon: Users          },
        { key: 'attrition',        label: 'Attrition',        icon: TrendingDown   },
        { key: 'department',       label: 'Departments',      icon: Building2      },
        { key: 'leave',            label: 'Leave',            icon: Calendar       },
        { key: 'attendance',       label: 'Attendance',       icon: Clock          },
        { key: 'payroll',          label: 'Payroll',          icon: CreditCard     },
        { key: 'salary_register',  label: 'Salary Register',  icon: IndianRupee    },  // NEW
        { key: 'timesheet',        label: 'Timesheets',       icon: FileText       },
        { key: 'hiring',           label: 'Hiring',           icon: Target         },
        { key: 'onboarding',       label: 'Onboarding',       icon: UserCheck      },
    ];

    return (
        <div className="p-2 p-md-4">
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                    <h4 className="fw-bold mb-1">
                        <BarChart2 size={22} className="me-2 text-primary"/>Reports & Analytics
                    </h4>
                    <p className="text-muted small mb-0">Workforce intelligence across all modules</p>
                </div>
                <div className="d-flex gap-2 align-items-center">
                    <Form.Select size="sm" value={year}
                        onChange={e => setYear(parseInt(e.target.value))}
                        style={{ width: 100 }}>
                        {[CURR_YEAR, CURR_YEAR-1, CURR_YEAR-2].map(y => <option key={y} value={y}>{y}</option>)}
                    </Form.Select>
                    {MONTH_TABS.includes(tab) && (
                        <Form.Select size="sm" value={month}
                            onChange={e => setMonth(parseInt(e.target.value))}
                            style={{ width: 110 }}>
                            {MONTHS.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                        </Form.Select>
                    )}
                </div>
            </div>

            <div className="d-flex gap-1 flex-wrap mb-4">
                {TABS.map(t => {
                    const Icon = t.icon;
                    return (
                        <Button key={t.key} size="sm"
                            variant={tab === t.key ? 'primary' : 'outline-secondary'}
                            onClick={() => setTab(t.key)}>
                            <Icon size={13} className="me-1"/>{t.label}
                        </Button>
                    );
                })}
            </div>

            {tab === 'overview'        && <OverviewTab year={year} month={month} />}
            {tab === 'headcount'       && <HeadcountTab year={year} />}
            {tab === 'attrition'       && <AttritionTab year={year} />}
            {tab === 'department'      && <DepartmentTab year={year} month={month} />}
            {tab === 'leave'           && <LeaveTab year={year} />}
            {tab === 'attendance'      && <AttendanceTab year={year} month={month} />}
            {tab === 'payroll'         && <PayrollTab year={year} month={month} />}
            {tab === 'salary_register' && <SalaryRegisterTab year={year} month={month} />}
            {tab === 'timesheet'       && <TimesheetTab year={year} month={month} />}
            {tab === 'hiring'          && <HiringTab year={year} />}
            {tab === 'onboarding'      && <OnboardingTab year={year} />}
        </div>
    );
};

// ── useReport hook ────────────────────────────────────────────────────────────
const useReport = (endpoint, deps = []) => {
    const [data,    setData]    = useState(null);
    const [loading, setLoading] = useState(true);
    const [error,   setError]   = useState(null);
    useEffect(() => {
        setLoading(true);
        axiosInstance.get(endpoint)
            .then(r => { setData(r.data.data); setError(null); })
            .catch(e => setError(e.response?.data?.message || 'Failed to load'))
            .finally(() => setLoading(false));
    }, deps); // eslint-disable-line
    return { data, loading, error };
};

const ReportShell = ({ loading, error, children }) => {
    if (loading) return <div className="text-center py-5"><Spinner animation="border" variant="primary"/></div>;
    if (error)   return <Alert variant="danger" className="small">{error}</Alert>;
    return children;
};

// ════════════════════════════════════════════════════════════════════════════
// 1. OVERVIEW — unchanged
// ════════════════════════════════════════════════════════════════════════════
const OverviewTab = ({ year, month }) => {
    const { data, loading, error } = useReport(`/reports/dashboard?year=${year}&month=${month}`, [year, month]);
    const { data: pending } = useReport('/reports/pending-approvals', []);
    return (
        <ReportShell loading={loading} error={error}>
            {data && (
                <>
                    <Row className="g-3 mb-4">
                        <Col xs={6} md={3}><KPICard label="Total Employees" value={fmt(data.headcount.total)} sub={`${data.headcount.joined_this_month} joined this month`} color="#4f46e5" icon={Users}/></Col>
                        <Col xs={6} md={3}><KPICard label="Attrition Rate (YTD)" value={`${data.attrition.attrition_rate}%`} sub={`${data.attrition.exits_ytd} exits`} color="#dc2626" icon={TrendingDown}/></Col>
                        <Col xs={6} md={3}><KPICard label="Avg Hours/Day" value={fmtH(data.attendance.avg_hours_per_day)} sub={`${fmt(data.attendance.late_count)} late this month`} color="#059669" icon={Clock}/></Col>
                        <Col xs={6} md={3}><KPICard label="Payroll (this month)" value={fmtC(data.payroll.total_net)} sub={`${fmt(data.payroll.payslip_count)} payslips`} color="#d97706" icon={CreditCard}/></Col>
                    </Row>
                    {pending && (
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Body className="p-4">
                                <SectionHeader title="Pending Approvals" subtitle="Items waiting for action"/>
                                <Row className="g-3">
                                    {[
                                        { label:'Leave requests',     val: pending.leaves,             color:'#d97706' },
                                        { label:'Timesheets',         val: pending.timesheets,         color:'#4f46e5' },
                                        { label:'Resignations',       val: pending.offboarding,        color:'#dc2626' },
                                        { label:'Overdue onboarding', val: pending.overdue_onboarding, color:'#0891b2' },
                                        { label:'Document requests',  val: pending.documents,          color:'#7c3aed' },
                                    ].map((item, i) => (
                                        <Col key={i} xs={6} md={4} lg={2}>
                                            <div className="p-3 rounded text-center" style={{ background: '#f8fafc', border: `2px solid ${item.color}20` }}>
                                                <div style={{ fontSize: 28, fontWeight: 700, color: item.color }}>{item.val || 0}</div>
                                                <div className="text-muted" style={{ fontSize: 11 }}>{item.label}</div>
                                                {item.val > 0 && <Badge bg="light" text="dark" style={{ fontSize: 9 }}>Action needed</Badge>}
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            </Card.Body>
                        </Card>
                    )}
                    <Row className="g-3">
                        <Col md={6}>
                            <Card className="border-0 shadow-sm h-100 p-4">
                                <SectionHeader title="Workforce Mix" subtitle="Employment type distribution"/>
                                <div className="d-flex gap-3 flex-wrap">
                                    {[
                                        { label:'Full Time', val: data.headcount.full_time, color:'#4f46e5' },
                                        { label:'Contract',  val: data.headcount.contract,  color:'#d97706' },
                                    ].map((t, i) => (
                                        <div key={i} className="text-center">
                                            <div style={{ fontSize: 28, fontWeight: 700, color: t.color }}>{t.val}</div>
                                            <div className="text-muted small">{t.label}</div>
                                        </div>
                                    ))}
                                </div>
                            </Card>
                        </Col>
                        <Col md={6}>
                            <Card className="border-0 shadow-sm h-100 p-4">
                                <SectionHeader title="Lifecycle Activity" subtitle="Current month"/>
                                <div className="d-flex gap-3 flex-wrap">
                                    {[
                                        { label:'Active onboarding',  val: data.workforce.active_onboarding,  color:'#059669', icon:'🚀' },
                                        { label:'Active offboarding', val: data.workforce.active_offboarding, color:'#dc2626', icon:'👋' },
                                        { label:'Open positions',     val: data.pending_approvals.open_jobs,  color:'#4f46e5', icon:'💼' },
                                    ].map((t, i) => (
                                        <div key={i} className="text-center">
                                            <div style={{ fontSize: 11 }}>{t.icon}</div>
                                            <div style={{ fontSize: 22, fontWeight: 700, color: t.color }}>{t.val}</div>
                                            <div className="text-muted" style={{ fontSize: 11 }}>{t.label}</div>
                                        </div>
                                    ))}
                                </div>
                            </Card>
                        </Col>
                    </Row>
                </>
            )}
        </ReportShell>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// 2. HEADCOUNT — unchanged
// ════════════════════════════════════════════════════════════════════════════
const HeadcountTab = ({ year }) => {
    const { data, loading, error } = useReport(`/reports/headcount?year=${year}`, [year]);
    return (
        <ReportShell loading={loading} error={error}>
            {data && (
                <>
                    <div className="d-flex justify-content-end mb-3">
                        <ExportBtn type="employees" year={year} label="Export Employees"/>
                    </div>
                    <Row className="g-4 mb-4">
                        <Col lg={8}>
                            <Card className="border-0 shadow-sm p-4">
                                <SectionHeader title="Monthly Hires vs Exits" subtitle="Rolling 12 months"/>
                                <ResponsiveContainer width="100%" height={260}>
                                    <BarChart data={mergeMonthly(data.monthly_hires, data.monthly_exits)}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                                        <XAxis dataKey="month" tickFormatter={mLabel} tick={{ fontSize: 11 }}/>
                                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false}/>
                                        <Tooltip labelFormatter={v => MONTHS[v-1]}/>
                                        <Legend/>
                                        <Bar dataKey="hires" name="New Hires" fill="#059669" radius={[4,4,0,0]}/>
                                        <Bar dataKey="exits" name="Exits" fill="#dc2626" radius={[4,4,0,0]}/>
                                    </BarChart>
                                </ResponsiveContainer>
                            </Card>
                        </Col>
                        <Col lg={4}>
                            <Card className="border-0 shadow-sm p-4 h-100">
                                <SectionHeader title="By Employment Type"/>
                                <ResponsiveContainer width="100%" height={220}>
                                    <PieChart>
                                        <Pie data={data.by_type} dataKey="count" nameKey="type" cx="50%" cy="50%" outerRadius={80} label={({type,count}) => `${type}: ${count}`}>
                                            {data.by_type.map((_, i) => <Cell key={i} fill={COLOURS[i % COLOURS.length]}/>)}
                                        </Pie>
                                        <Tooltip/>
                                    </PieChart>
                                </ResponsiveContainer>
                            </Card>
                        </Col>
                    </Row>
                    <Card className="border-0 shadow-sm p-4">
                        <SectionHeader title="Headcount by Department"/>
                        <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={data.by_department} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false}/>
                                <YAxis type="category" dataKey="department" tick={{ fontSize: 11 }} width={120}/>
                                <Tooltip/>
                                <Bar dataKey="count" name="Employees" fill="#4f46e5" radius={[0,4,4,0]}/>
                            </BarChart>
                        </ResponsiveContainer>
                    </Card>
                </>
            )}
        </ReportShell>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// 3. ATTRITION — unchanged
// ════════════════════════════════════════════════════════════════════════════
const AttritionTab = ({ year }) => {
    const { data, loading, error } = useReport(`/reports/attrition?year=${year}`, [year]);
    return (
        <ReportShell loading={loading} error={error}>
            {data && (
                <>
                    <div className="d-flex justify-content-end mb-3"><ExportBtn type="attrition" year={year}/></div>
                    <Row className="g-4 mb-4">
                        {data.avg_scores && (
                            <>
                                <Col xs={6} md={3}><KPICard label="Avg Experience Rating" value={`${data.avg_scores.avg_experience || '—'}/5`} color="#4f46e5"/></Col>
                                <Col xs={6} md={3}><KPICard label="Avg eNPS Score" value={data.avg_scores.avg_enps || '—'} color="#059669" sub="0-10"/></Col>
                                <Col xs={6} md={3}><KPICard label="Avg Manager Rating" value={`${data.avg_scores.avg_manager || '—'}/5`} color="#d97706"/></Col>
                                <Col xs={6} md={3}><KPICard label="Would Return" value={`${data.avg_scores.would_return || 0}`} sub={`of ${data.avg_scores.total_interviews} interviews`} color="#0891b2"/></Col>
                            </>
                        )}
                    </Row>
                    <Row className="g-4">
                        <Col lg={8}>
                            <Card className="border-0 shadow-sm p-4">
                                <SectionHeader title="Monthly Exits" subtitle={`${year}`}/>
                                <ResponsiveContainer width="100%" height={240}>
                                    <BarChart data={rollupByMonth(data.monthly, 'exits')}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                                        <XAxis dataKey="month" tickFormatter={mLabel} tick={{ fontSize: 11 }}/>
                                        <YAxis allowDecimals={false} tick={{ fontSize: 11 }}/>
                                        <Tooltip labelFormatter={v => MONTHS[v-1]}/>
                                        <Bar dataKey="exits" name="Exits" fill="#dc2626" radius={[4,4,0,0]}/>
                                    </BarChart>
                                </ResponsiveContainer>
                            </Card>
                        </Col>
                        <Col lg={4}>
                            <Card className="border-0 shadow-sm p-4 h-100">
                                <SectionHeader title="Exit Reasons"/>
                                {data.reasons?.length ? (
                                    data.reasons.map((r, i) => (
                                        <div key={i} className="mb-2">
                                            <div className="d-flex justify-content-between small mb-1"><span>{r.reason_category}</span><span className="fw-bold">{r.count}</span></div>
                                            <div style={{ height: 6, background: '#e5e7eb', borderRadius: 3 }}>
                                                <div style={{ width: `${(r.count / data.reasons[0].count) * 100}%`, height: '100%', background: COLOURS[i % COLOURS.length], borderRadius: 3, transition: 'width .4s' }}/>
                                            </div>
                                        </div>
                                    ))
                                ) : <p className="text-muted small">No exits this year</p>}
                            </Card>
                        </Col>
                    </Row>
                </>
            )}
        </ReportShell>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// 4. DEPARTMENTS — unchanged
// ════════════════════════════════════════════════════════════════════════════
const DepartmentTab = ({ year, month }) => {
    const { data, loading, error } = useReport(`/reports/department?year=${year}&month=${month}`, [year, month]);
    return (
        <ReportShell loading={loading} error={error}>
            {data && (
                <>
                    <Row className="g-4 mb-4">
                        <Col lg={6}>
                            <Card className="border-0 shadow-sm p-4">
                                <SectionHeader title="Headcount by Department"/>
                                <ResponsiveContainer width="100%" height={260}>
                                    <BarChart data={data} layout="vertical">
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                                        <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false}/>
                                        <YAxis type="category" dataKey="department" tick={{ fontSize: 11 }} width={120}/>
                                        <Tooltip/>
                                        <Bar dataKey="headcount" fill="#4f46e5" radius={[0,4,4,0]}/>
                                    </BarChart>
                                </ResponsiveContainer>
                            </Card>
                        </Col>
                        <Col lg={6}>
                            <Card className="border-0 shadow-sm p-4">
                                <SectionHeader title="Avg Salary by Department" subtitle="Gross"/>
                                <ResponsiveContainer width="100%" height={260}>
                                    <BarChart data={data} layout="vertical">
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                                        <XAxis type="number" tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11 }}/>
                                        <YAxis type="category" dataKey="department" tick={{ fontSize: 11 }} width={120}/>
                                        <Tooltip formatter={v => fmtC(v)}/>
                                        <Bar dataKey="avg_gross_salary" name="Avg Gross" fill="#059669" radius={[0,4,4,0]}/>
                                    </BarChart>
                                </ResponsiveContainer>
                            </Card>
                        </Col>
                    </Row>
                    <Card className="border-0 shadow-sm">
                        <Table hover responsive className="mb-0 small">
                            <thead className="bg-light">
                                <tr className="text-muted text-uppercase" style={{ fontSize: 11 }}>
                                    <th className="ps-4">Department</th><th>Headcount</th><th>Total Payroll</th>
                                    <th>Avg Salary</th><th>Leaves (YTD)</th><th className="pe-4">Pending Leaves</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((d, i) => (
                                    <tr key={i}>
                                        <td className="ps-4 fw-medium">{d.department}</td>
                                        <td>{d.headcount}</td>
                                        <td>{fmtC(d.total_gross_salary)}</td>
                                        <td>{fmtC(d.avg_gross_salary)}</td>
                                        <td>{d.approved_leaves || 0}</td>
                                        <td className="pe-4">
                                            {d.pending_leaves > 0 ? <Badge bg="warning">{d.pending_leaves}</Badge> : <span className="text-muted">—</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card>
                </>
            )}
        </ReportShell>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// 5. LEAVE — unchanged
// ════════════════════════════════════════════════════════════════════════════
const LeaveTab = ({ year }) => {
    const { data, loading, error } = useReport(`/reports/leave-utilisation?year=${year}`, [year]);
    return (
        <ReportShell loading={loading} error={error}>
            {data && (
                <>
                    <div className="d-flex justify-content-end mb-3"><ExportBtn type="leave" year={year}/></div>
                    {data.approval_rate && (
                        <Row className="g-3 mb-4">
                            {[
                                { label:'Total Requests', val: fmt(data.approval_rate.total),    color:'#4f46e5' },
                                { label:'Approved',       val: fmt(data.approval_rate.approved),  color:'#059669' },
                                { label:'Pending',        val: fmt(data.approval_rate.pending),   color:'#d97706' },
                                { label:'Approval Rate',  val: `${data.approval_rate.approval_pct||0}%`, color:'#0891b2' },
                            ].map((k, i) => <Col key={i} xs={6} md={3}><KPICard {...k} value={k.val}/></Col>)}
                        </Row>
                    )}
                    <Row className="g-4 mb-4">
                        <Col lg={8}>
                            <Card className="border-0 shadow-sm p-4">
                                <SectionHeader title="Monthly Leave Days by Type"/>
                                <ResponsiveContainer width="100%" height={260}>
                                    <BarChart data={pivotLeaveByMonth(data.by_month)}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                                        <XAxis dataKey="month" tickFormatter={mLabel} tick={{ fontSize: 11 }}/>
                                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false}/>
                                        <Tooltip labelFormatter={v => MONTHS[v-1]}/>
                                        <Legend/>
                                        {getLeaveTypes(data.by_month).map((type, i) => (
                                            <Bar key={type} dataKey={type} stackId="a" fill={COLOURS[i % COLOURS.length]} radius={i===0?[0,0,4,4]:[0,0,0,0]}/>
                                        ))}
                                    </BarChart>
                                </ResponsiveContainer>
                            </Card>
                        </Col>
                        <Col lg={4}>
                            <Card className="border-0 shadow-sm p-4 h-100">
                                <SectionHeader title="Top 10 Leave Takers"/>
                                {(data.top_takers || []).slice(0, 8).map((t, i) => (
                                    <div key={i} className="d-flex justify-content-between align-items-center mb-2 small">
                                        <div>
                                            <div className="fw-medium">{t.employee}</div>
                                            <div className="text-muted" style={{ fontSize: 10 }}>{t.department}</div>
                                        </div>
                                        <Badge bg="light" text="dark">{t.days}d</Badge>
                                    </div>
                                ))}
                            </Card>
                        </Col>
                    </Row>
                </>
            )}
        </ReportShell>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// 6. ATTENDANCE — unchanged
// ════════════════════════════════════════════════════════════════════════════
const AttendanceTab = ({ year, month }) => {
    const { data, loading, error } = useReport(`/reports/attendance?year=${year}&month=${month}`, [year, month]);
    return (
        <ReportShell loading={loading} error={error}>
            {data && (
                <>
                    <div className="d-flex justify-content-end mb-3"><ExportBtn type="attendance" year={year} month={month}/></div>
                    <Row className="g-4 mb-4">
                        <Col lg={8}>
                            <Card className="border-0 shadow-sm p-4">
                                <SectionHeader title="Monthly Attendance %" subtitle={`${year}`}/>
                                <ResponsiveContainer width="100%" height={240}>
                                    <AreaChart data={data.monthly}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                                        <XAxis dataKey="month" tickFormatter={mLabel} tick={{ fontSize: 11 }}/>
                                        <YAxis domain={[0,100]} tick={{ fontSize: 11 }} unit="%"/>
                                        <Tooltip labelFormatter={v => MONTHS[v-1]}/>
                                        <Area type="monotone" dataKey="attendance_pct" name="Attendance %" stroke="#059669" fill="#dcfce7" strokeWidth={2}/>
                                    </AreaChart>
                                </ResponsiveContainer>
                            </Card>
                        </Col>
                        <Col lg={4}>
                            <Card className="border-0 shadow-sm p-4 h-100">
                                <SectionHeader title="Hours Distribution" subtitle={`${MONTHS[month-1]} ${year}`}/>
                                {data.hours_distribution && (
                                    <ResponsiveContainer width="100%" height={160}>
                                        <PieChart>
                                            <Pie data={[
                                                { name: '< 4h', value: parseInt(data.hours_distribution.under_4h) || 0 },
                                                { name: '4–8h', value: parseInt(data.hours_distribution.four_to_8h) || 0 },
                                                { name: '8h+',  value: parseInt(data.hours_distribution.over_8h) || 0 },
                                            ]} dataKey="value" cx="50%" cy="50%" outerRadius={65} label={({name,value}) => value ? `${name}: ${value}` : ''}>
                                                {[0,1,2].map(i => <Cell key={i} fill={['#dc2626','#d97706','#059669'][i]}/>)}
                                            </Pie>
                                            <Tooltip/>
                                        </PieChart>
                                    </ResponsiveContainer>
                                )}
                            </Card>
                        </Col>
                    </Row>
                    {data.most_absent?.length > 0 && (
                        <Card className="border-0 shadow-sm">
                            <Card.Header className="bg-white fw-bold border-0 pt-3">Absenteeism — {MONTHS[month-1]} {year}</Card.Header>
                            <Table hover responsive className="mb-0 small">
                                <thead className="bg-light">
                                    <tr className="text-muted text-uppercase" style={{ fontSize: 11 }}>
                                        <th className="ps-4">Employee</th><th>Dept</th><th>Absent</th><th>Late</th><th className="pe-4">Total Days</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.most_absent.map((e, i) => (
                                        <tr key={i}>
                                            <td className="ps-4"><div className="fw-medium">{e.employee}</div><div className="text-muted" style={{ fontSize: 10 }}>{e.employee_code}</div></td>
                                            <td>{e.department || '—'}</td>
                                            <td><Badge bg="danger">{e.absent_days}</Badge></td>
                                            <td><Badge bg="warning">{e.late_days}</Badge></td>
                                            <td className="pe-4">{e.total_days}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card>
                    )}
                </>
            )}
        </ReportShell>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// 7. PAYROLL — unchanged
// ════════════════════════════════════════════════════════════════════════════
const PayrollTab = ({ year, month }) => {
    const { data, loading, error } = useReport(`/reports/payroll-cost?year=${year}&month=${month}`, [year, month]);
    return (
        <ReportShell loading={loading} error={error}>
            {data && (
                <>
                    <div className="d-flex justify-content-end mb-3"><ExportBtn type="payroll" year={year} month={month}/></div>
                    {data.ytd && (
                        <Row className="g-3 mb-4">
                            {[
                                { label:'YTD Gross', val: fmtC(data.ytd.ytd_gross), color:'#4f46e5' },
                                { label:'YTD Net',   val: fmtC(data.ytd.ytd_net),   color:'#059669' },
                                { label:'YTD TDS',   val: fmtC(data.ytd.ytd_tds),   color:'#d97706' },
                                { label:'YTD PF',    val: fmtC(data.ytd.ytd_pf),    color:'#0891b2' },
                            ].map((k, i) => <Col key={i} xs={6} md={3}><KPICard {...k} value={k.val}/></Col>)}
                        </Row>
                    )}
                    <Row className="g-4 mb-4">
                        <Col lg={8}>
                            <Card className="border-0 shadow-sm p-4">
                                <SectionHeader title="Monthly Payroll Cost" subtitle={`${year}`}/>
                                <ResponsiveContainer width="100%" height={240}>
                                    <BarChart data={data.monthly}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                                        <XAxis dataKey="month" tickFormatter={mLabel} tick={{ fontSize: 11 }}/>
                                        <YAxis tickFormatter={v => `₹${(v/100000).toFixed(1)}L`} tick={{ fontSize: 11 }}/>
                                        <Tooltip formatter={v => fmtC(v)}/>
                                        <Legend/>
                                        <Bar dataKey="total_gross" name="Gross" fill="#4f46e5" radius={[4,4,0,0]}/>
                                        <Bar dataKey="total_net"   name="Net"   fill="#059669" radius={[4,4,0,0]}/>
                                    </BarChart>
                                </ResponsiveContainer>
                            </Card>
                        </Col>
                        <Col lg={4}>
                            <Card className="border-0 shadow-sm p-4 h-100">
                                <SectionHeader title="Department Cost" subtitle={`${MONTHS[month-1]} ${year}`}/>
                                {(data.by_department || []).slice(0, 6).map((d, i) => (
                                    <div key={i} className="mb-2">
                                        <div className="d-flex justify-content-between small mb-1">
                                            <span className="text-truncate" style={{ maxWidth: 120 }}>{d.department}</span>
                                            <span className="fw-medium">{fmtC(d.total_gross)}</span>
                                        </div>
                                        <div style={{ height: 5, background: '#e5e7eb', borderRadius: 3 }}>
                                            <div style={{ width: `${data.by_department[0]?.total_gross ? (d.total_gross / data.by_department[0].total_gross) * 100 : 0}%`, height: '100%', background: COLOURS[i % COLOURS.length], borderRadius: 3 }}/>
                                        </div>
                                    </div>
                                ))}
                            </Card>
                        </Col>
                    </Row>
                </>
            )}
        </ReportShell>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// 8. SALARY REGISTER — NEW
// ════════════════════════════════════════════════════════════════════════════
const SalaryRegisterTab = ({ year, month }) => {
    const { data, loading, error } = useReport(
        `/reports/salary-register?year=${year}&month=${month}`, [year, month]
    );
    const [xlsxLoading, setXlsxLoading] = useState(false);

    // ── Excel download using SheetJS (xlsx is available in PeoplesDock deps) ──
    const downloadExcel = () => {
        if (!data?.rows?.length) return toast.error('No data to export');
        setXlsxLoading(true);
        try {
            const monthLabel = MONTHS_FULL[month - 1];
            const generated  = new Date().toLocaleDateString('en-IN');

            // Helper: escape cell value for CSV
            const esc = (v) => {
                if (v === null || v === undefined) return '';
                const s = String(v);
                return (s.includes(',') || s.includes('"') || s.includes('\n'))
                    ? `"${s.replace(/"/g, '""')}"` : s;
            };
            const amt = (n) => parseFloat(n || 0).toFixed(2);

            const rows = [];

            // ── Header info ───────────────────────────────────────────────
            rows.push([`Salary Register — ${monthLabel} ${year}`]);
            rows.push([`Generated: ${generated}`, `Employees: ${data.employee_count}`]);
            rows.push([]);

            // ── Column headers ────────────────────────────────────────────
            rows.push([
                'Emp Code', 'Employee Name', 'Department', 'Designation',
                'Employment Type', 'Location', 'Join Date',
                'Basic Salary', 'HRA', 'Special Allow.', 'Other Allow.',
                'Gross Salary',
                'PF Deduction', 'ESI', 'TDS Deduction', 'PT Deduction',
                'Loan Ded.', 'Other Ded.', 'Total Deductions',
                'Net Salary', 'Payment Mode', 'Payment Date', 'Status',
            ]);

            // ── Data rows ─────────────────────────────────────────────────
            data.rows.forEach(r => {
                rows.push([
                    r.employee_code      || '',
                    r.employee_name      || '',
                    r.department         || '',
                    r.designation        || '',
                    r.employment_type    || '',
                    r.location           || '',
                    r.join_date ? new Date(r.join_date).toLocaleDateString('en-IN') : '',
                    amt(r.basic_salary),
                    amt(r.hra),
                    amt(r.special_allowance),
                    amt(r.other_allowances),
                    amt(r.gross_salary),
                    amt(r.pf_deduction),
                    amt(r.esi_deduction),
                    amt(r.tds_deduction),
                    amt(r.pt_deduction),
                    amt(r.loan_deduction),
                    amt(r.other_deductions),
                    amt(r.total_deductions),
                    amt(r.net_salary),
                    r.payment_mode   || '',
                    r.payment_date   || '',
                    r.payroll_status || '',
                ]);
            });

            // ── Totals row ────────────────────────────────────────────────
            rows.push([
                'TOTAL', `${data.employee_count} employees`, '', '', '', '', '',
                amt(data.totals.basic),
                '0.00', '0.00', '0.00',
                amt(data.totals.gross),
                amt(data.totals.pf),
                '0.00',
                amt(data.totals.tds),
                amt(data.totals.pt),
                '0.00', '0.00',
                amt(data.totals.total_deductions),
                amt(data.totals.net),
                '', '', '',
            ]);

            // ── Department Summary (appended below) ───────────────────────
            rows.push([]);
            rows.push([]);
            rows.push([`Department Summary — ${monthLabel} ${year}`]);
            rows.push(['Department', 'Employees', 'Total Gross', 'Total Net',
                       'Total TDS', 'Total PF', 'Total ESI']);
            data.by_department.forEach(d => {
                rows.push([
                    d.department,
                    d.count,
                    amt(d.gross),
                    amt(d.net),
                    amt(d.tds),
                    amt(d.pf),
                    '0.00',
                ]);
            });
            rows.push([
                'TOTAL', data.employee_count,
                amt(data.totals.gross),
                amt(data.totals.net),
                amt(data.totals.tds),
                amt(data.totals.pf),
                '0.00',
            ]);

            // ── Build CSV string ──────────────────────────────────────────
            const csv = rows.map(row => row.map(esc).join(',')).join('\r\n');

            // UTF-8 BOM so Excel opens with correct encoding (Indian names, ₹ symbol)
            const BOM  = '\uFEFF';
            const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
            const url  = URL.createObjectURL(blob);
            const a    = document.createElement('a');
            a.href     = url;
            a.download = `Salary_Register_${year}_${String(month).padStart(2,'0')}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            toast.success(`Salary_Register_${year}_${String(month).padStart(2,'0')}.csv downloaded — open with Excel`);
        } catch (err) {
            console.error('Download error:', err);
            toast.error('Download failed. Please try again.');
        } finally {
            setXlsxLoading(false);
        }
    };
    const fmtAmt = (n) => parseFloat(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

    return (
        <ReportShell loading={loading} error={error}>
            {data && (
                <>
                    {/* Top bar */}
                    <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                        <div>
                            <h6 className="fw-bold mb-0">
                                Salary Register — {MONTHS_FULL[month - 1]} {year}
                            </h6>
                            <p className="text-muted small mb-0">
                                {data.employee_count} employee{data.employee_count !== 1 ? 's' : ''}
                                {data.employee_count === 0 && ' — no payroll processed for this month'}
                            </p>
                        </div>
                        <Button
                            variant="success"
                            size="sm"
                            onClick={downloadExcel}
                            disabled={xlsxLoading || !data.rows.length}
                            style={{ borderRadius: 8 }}>
                            {xlsxLoading
                                ? <><Spinner size="sm" className="me-2"/>Generating…</>
                                : <><Download size={14} className="me-2"/>Download CSV (opens in Excel)</>
                            }
                        </Button>
                    </div>

                    {/* Empty state */}
                    {data.rows.length === 0 ? (
                        <Card className="border-0 shadow-sm">
                            <Card.Body className="text-center py-5 text-muted">
                                <IndianRupee size={40} className="mb-3 opacity-30"/>
                                <h6 className="fw-semibold">No payroll data for {MONTHS_FULL[month - 1]} {year}</h6>
                                <p className="small">Payroll has not been processed for this month yet.</p>
                            </Card.Body>
                        </Card>
                    ) : (
                        <>
                            {/* KPI summary row */}
                            <Row className="g-3 mb-3">
                                {[
                                    { label: 'Total Gross',       val: fmtC(data.totals.gross), color: '#4f46e5' },
                                    { label: 'Total Net Pay',     val: fmtC(data.totals.net),   color: '#059669' },
                                    { label: 'Total TDS',         val: fmtC(data.totals.tds),   color: '#d97706' },
                                    { label: 'Total PF',          val: fmtC(data.totals.pf),    color: '#0891b2' },
                                    { label: 'Total ESI',         val: fmtC(data.totals.esi),   color: '#7c3aed' },
                                    { label: 'Total Deductions',  val: fmtC(data.totals.total_deductions), color: '#dc2626' },
                                ].map((k, i) => (
                                    <Col key={i} xs={6} md={4} lg={2}>
                                        <Card className="border-0 shadow-sm h-100 p-3" style={{ borderLeft: `3px solid ${k.color}` }}>
                                            <div className="text-muted" style={{ fontSize: 10, textTransform: 'uppercase', fontWeight: 600 }}>{k.label}</div>
                                            <div style={{ fontSize: 16, fontWeight: 700, color: k.color, marginTop: 4 }}>{k.val}</div>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>

                            {/* Main register table */}
                            <Card className="border-0 shadow-sm mb-3">
                                <div style={{ overflowX: 'auto' }}>
                                    <Table className="mb-0 align-middle" style={{ fontSize: 12, minWidth: 1200 }}>
                                        <thead style={{ background: '#4f46e5', color: '#fff' }}>
                                            <tr>
                                                {/* Identity */}
                                                <th className="ps-3 py-2" style={{ whiteSpace: 'nowrap' }}>Emp Code</th>
                                                <th style={{ whiteSpace: 'nowrap' }}>Name</th>
                                                <th style={{ whiteSpace: 'nowrap' }}>Department</th>
                                                <th style={{ whiteSpace: 'nowrap' }}>Designation</th>
                                                {/* Earnings */}
                                                <th className="text-end" style={{ whiteSpace: 'nowrap', background: '#3730a3' }}>Basic</th>
                                                <th className="text-end" style={{ whiteSpace: 'nowrap', background: '#3730a3' }}>HRA</th>
                                                <th className="text-end" style={{ whiteSpace: 'nowrap', background: '#3730a3' }}>Special</th>
                                                <th className="text-end fw-bold" style={{ whiteSpace: 'nowrap', background: '#1e1b4b' }}>Gross</th>
                                                {/* Deductions */}
                                                <th className="text-end" style={{ whiteSpace: 'nowrap', background: '#7f1d1d' }}>PF</th>
                                                <th className="text-end" style={{ whiteSpace: 'nowrap', background: '#7f1d1d' }}>ESI</th>
                                                <th className="text-end" style={{ whiteSpace: 'nowrap', background: '#7f1d1d' }}>TDS</th>
                                                <th className="text-end" style={{ whiteSpace: 'nowrap', background: '#7f1d1d' }}>PT</th>
                                                <th className="text-end" style={{ whiteSpace: 'nowrap', background: '#7f1d1d' }}>Loan</th>
                                                <th className="text-end fw-bold" style={{ whiteSpace: 'nowrap', background: '#450a0a' }}>Total Ded.</th>
                                                {/* Net */}
                                                <th className="text-end fw-bold pe-3" style={{ whiteSpace: 'nowrap', background: '#14532d' }}>Net Pay</th>
                                                {/* Status */}
                                                <th style={{ whiteSpace: 'nowrap' }}>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.rows.map((r, i) => (
                                                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                                    <td className="ps-3 text-muted" style={{ fontFamily: 'monospace', fontSize: 11 }}>{r.employee_code || '—'}</td>
                                                    <td>
                                                        <div className="fw-semibold">{r.employee_name}</div>
                                                        <div className="text-muted" style={{ fontSize: 10 }}>{r.employment_type}</div>
                                                    </td>
                                                    <td>{r.department}</td>
                                                    <td>{r.designation}</td>
                                                    {/* Earnings */}
                                                    <td className="text-end">{fmtAmt(r.basic_salary)}</td>
                                                    <td className="text-end">{fmtAmt(r.hra)}</td>
                                                    <td className="text-end">{fmtAmt(r.special_allowance)}</td>
                                                    <td className="text-end fw-bold" style={{ color: '#4f46e5' }}>₹{fmtAmt(r.gross_salary)}</td>
                                                    {/* Deductions */}
                                                    <td className="text-end text-danger">{fmtAmt(r.pf_deduction)}</td>
                                                    <td className="text-end text-danger">{fmtAmt(r.esi_deduction)}</td>
                                                    <td className="text-end text-danger">{fmtAmt(r.tds_deduction)}</td>
                                                    <td className="text-end text-danger">{fmtAmt(r.pt_deduction)}</td>
                                                    <td className="text-end text-danger">{fmtAmt(r.loan_deduction)}</td>
                                                    <td className="text-end fw-bold text-danger">₹{fmtAmt(r.total_deductions)}</td>
                                                    {/* Net */}
                                                    <td className="text-end fw-bold pe-3" style={{ color: '#059669' }}>₹{fmtAmt(r.net_salary)}</td>
                                                    <td>
                                                        <Badge bg={r.payroll_status === 'paid' ? 'success' : r.payroll_status === 'pending' ? 'warning' : 'secondary'} style={{ fontSize: 10 }}>
                                                            {r.payroll_status || '—'}
                                                        </Badge>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        {/* TOTALS ROW */}
                                        <tfoot>
                                            <tr style={{ background: 'linear-gradient(90deg, #4f46e5, #3730a3)', color: '#fff', fontWeight: 700, fontSize: 13 }}>
                                                <td className="ps-3 py-2" colSpan={4}>TOTAL — {data.employee_count} Employees</td>
                                                <td className="text-end">₹{fmtAmt(data.totals.basic)}</td>
                                                <td className="text-end">₹{fmtAmt(data.totals.hra)}</td>
                                                <td className="text-end">₹{fmtAmt(data.totals.special)}</td>
                                                <td className="text-end">₹{fmtAmt(data.totals.gross)}</td>
                                                <td className="text-end">₹{fmtAmt(data.totals.pf)}</td>
                                                <td className="text-end">₹{fmtAmt(data.totals.esi)}</td>
                                                <td className="text-end">₹{fmtAmt(data.totals.tds)}</td>
                                                <td className="text-end">₹{fmtAmt(data.totals.pt)}</td>
                                                <td className="text-end">₹{fmtAmt(data.totals.loan)}</td>
                                                <td className="text-end">₹{fmtAmt(data.totals.total_deductions)}</td>
                                                <td className="text-end pe-3">₹{fmtAmt(data.totals.net)}</td>
                                                <td></td>
                                            </tr>
                                        </tfoot>
                                    </Table>
                                </div>
                            </Card>

                            {/* Department Summary */}
                            {data.by_department.length > 0 && (
                                <Card className="border-0 shadow-sm">
                                    <Card.Header className="bg-white fw-bold border-0 pt-3">
                                        Department-wise Summary
                                    </Card.Header>
                                    <Table hover responsive className="mb-0 small align-middle">
                                        <thead className="bg-light">
                                            <tr className="text-muted text-uppercase" style={{ fontSize: 11 }}>
                                                <th className="ps-4">Department</th>
                                                <th className="text-center">Employees</th>
                                                <th className="text-end">Total Gross</th>
                                                <th className="text-end">Total TDS</th>
                                                <th className="text-end">Total PF</th>
                                                <th className="text-end">Total ESI</th>
                                                <th className="text-end pe-4">Total Net</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.by_department.map((d, i) => (
                                                <tr key={i}>
                                                    <td className="ps-4 fw-semibold">{d.department}</td>
                                                    <td className="text-center">{d.count}</td>
                                                    <td className="text-end">{fmtC(d.gross)}</td>
                                                    <td className="text-end text-danger">{fmtC(d.tds)}</td>
                                                    <td className="text-end text-danger">{fmtC(d.pf)}</td>
                                                    <td className="text-end text-danger">{fmtC(d.esi)}</td>
                                                    <td className="text-end pe-4 fw-bold text-success">{fmtC(d.net)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr style={{ background: '#f0f4ff', fontWeight: 700 }}>
                                                <td className="ps-4">TOTAL</td>
                                                <td className="text-center">{data.employee_count}</td>
                                                <td className="text-end">{fmtC(data.totals.gross)}</td>
                                                <td className="text-end text-danger">{fmtC(data.totals.tds)}</td>
                                                <td className="text-end text-danger">{fmtC(data.totals.pf)}</td>
                                                <td className="text-end text-danger">{fmtC(data.totals.esi)}</td>
                                                <td className="text-end pe-4 text-success">{fmtC(data.totals.net)}</td>
                                            </tr>
                                        </tfoot>
                                    </Table>
                                </Card>
                            )}
                        </>
                    )}
                </>
            )}
        </ReportShell>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// 9. TIMESHEETS — unchanged
// ════════════════════════════════════════════════════════════════════════════
const TimesheetTab = ({ year, month }) => {
    const { data, loading, error } = useReport(`/reports/timesheet?year=${year}&month=${month}`, [year, month]);
    return (
        <ReportShell loading={loading} error={error}>
            {data && (
                <>
                    <div className="d-flex justify-content-end mb-3"><ExportBtn type="timesheets" year={year} month={month}/></div>
                    <Row className="g-4 mb-4">
                        <Col lg={8}>
                            <Card className="border-0 shadow-sm p-4">
                                <SectionHeader title="Monthly Hours Logged" subtitle={`${year}`}/>
                                <ResponsiveContainer width="100%" height={240}>
                                    <AreaChart data={data.monthly}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                                        <XAxis dataKey="month" tickFormatter={mLabel} tick={{ fontSize: 11 }}/>
                                        <YAxis tick={{ fontSize: 11 }}/>
                                        <Tooltip labelFormatter={v => MONTHS[v-1]}/>
                                        <Area type="monotone" dataKey="total_hours" name="Hours" stroke="#4f46e5" fill="#eef2ff" strokeWidth={2}/>
                                    </AreaChart>
                                </ResponsiveContainer>
                            </Card>
                        </Col>
                        <Col lg={4}>
                            <Card className="border-0 shadow-sm p-4 h-100">
                                <SectionHeader title="Top Projects" subtitle={`${MONTHS[month-1]} ${year}`}/>
                                {(data.by_project || []).slice(0, 7).map((p, i) => (
                                    <div key={i} className="d-flex justify-content-between align-items-center mb-2 small">
                                        <div className="text-truncate" style={{ maxWidth: 140 }}>{p.project}</div>
                                        <Badge bg="light" text="dark">{parseFloat(p.hours).toFixed(1)}h</Badge>
                                    </div>
                                ))}
                            </Card>
                        </Col>
                    </Row>
                </>
            )}
        </ReportShell>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// 10. HIRING FUNNEL — unchanged
// ════════════════════════════════════════════════════════════════════════════
const HiringTab = ({ year }) => {
    const { data, loading, error } = useReport(`/reports/hiring?year=${year}`, [year]);
    return (
        <ReportShell loading={loading} error={error}>
            {data && data.funnel && (
                <>
                    <Row className="g-3 mb-4">
                        {[
                            { label:'Applied',     val: data.funnel.applied,    color:'#4f46e5' },
                            { label:'Interviewed', val: data.funnel.interviews, color:'#d97706' },
                            { label:'Offers Made', val: data.funnel.offers,     color:'#0891b2' },
                            { label:'Hired',       val: data.funnel.hired,      color:'#059669' },
                        ].map((k, i) => <Col key={i} xs={6} md={3}><KPICard {...k} value={fmt(k.val)}/></Col>)}
                    </Row>
                    <Row className="g-4">
                        <Col lg={8}>
                            <Card className="border-0 shadow-sm p-4">
                                <SectionHeader title="Monthly Applications vs Hires"/>
                                <ResponsiveContainer width="100%" height={240}>
                                    <BarChart data={data.monthly}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                                        <XAxis dataKey="month" tickFormatter={mLabel} tick={{ fontSize: 11 }}/>
                                        <YAxis allowDecimals={false} tick={{ fontSize: 11 }}/>
                                        <Tooltip labelFormatter={v => MONTHS[v-1]}/>
                                        <Legend/>
                                        <Bar dataKey="applications" name="Applications" fill="#4f46e5" radius={[4,4,0,0]}/>
                                        <Bar dataKey="hired"        name="Hired"        fill="#059669" radius={[4,4,0,0]}/>
                                    </BarChart>
                                </ResponsiveContainer>
                            </Card>
                        </Col>
                        <Col lg={4}>
                            <Card className="border-0 shadow-sm p-4 h-100">
                                <SectionHeader title="Open Positions by Category"/>
                                {(data.open_jobs || []).map((j, i) => (
                                    <div key={i} className="d-flex justify-content-between small mb-2">
                                        <span>{j.category}</span>
                                        <Badge bg="primary">{j.count}</Badge>
                                    </div>
                                ))}
                                {!data.open_jobs?.length && <p className="text-muted small">No open positions</p>}
                            </Card>
                        </Col>
                    </Row>
                </>
            )}
        </ReportShell>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// 11. ONBOARDING — unchanged
// ════════════════════════════════════════════════════════════════════════════
const OnboardingTab = ({ year }) => {
    const { data, loading, error } = useReport(`/reports/onboarding?year=${year}`, [year]);
    return (
        <ReportShell loading={loading} error={error}>
            {data && (
                <>
                    {data.summary && (
                        <Row className="g-3 mb-4">
                            {[
                                { label:'Total Started',        val: data.summary.total,               color:'#4f46e5' },
                                { label:'Completed',            val: data.summary.completed,            color:'#059669' },
                                { label:'In Progress',          val: data.summary.in_progress,          color:'#d97706' },
                                { label:'Avg Days to Complete', val: data.summary.avg_days_to_complete || '—', color:'#0891b2' },
                            ].map((k, i) => <Col key={i} xs={6} md={3}><KPICard {...k} value={fmt(k.val)}/></Col>)}
                        </Row>
                    )}
                    <Row className="g-4">
                        <Col lg={6}>
                            <Card className="border-0 shadow-sm p-4">
                                <SectionHeader title="Task Completion by Stage"/>
                                {(data.by_stage || []).map((s, i) => (
                                    <div key={i} className="mb-3">
                                        <div className="d-flex justify-content-between small mb-1">
                                            <span className="fw-medium">{s.stage.replace('_',' ')}</span>
                                            <span>{s.done}/{s.total} ({s.pct}%)</span>
                                        </div>
                                        <div style={{ height: 8, background: '#e5e7eb', borderRadius: 4 }}>
                                            <div style={{ width: `${s.pct}%`, height: '100%', background: COLOURS[i], borderRadius: 4, transition: 'width .4s' }}/>
                                        </div>
                                    </div>
                                ))}
                            </Card>
                        </Col>
                        <Col lg={6}>
                            <Card className="border-0 shadow-sm p-4 h-100">
                                <SectionHeader title="Overdue Tasks" subtitle="Current employees with overdue items"/>
                                {data.overdue?.length ? (
                                    data.overdue.map((e, i) => (
                                        <div key={i} className="d-flex justify-content-between align-items-center mb-2 small">
                                            <span>{e.employee}</span>
                                            <Badge bg="danger">{e.overdue_tasks} overdue</Badge>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-success small">
                                        <CheckCircle size={14} className="me-1"/>No overdue tasks
                                    </div>
                                )}
                            </Card>
                        </Col>
                    </Row>
                </>
            )}
        </ReportShell>
    );
};

// ── Data helpers — unchanged ───────────────────────────────────────────────
function mergeMonthly(hires = [], exits = []) {
    const m = {};
    hires.forEach(h => { m[`${h.year}-${h.month}`] = { month: h.month, year: h.year, hires: h.count, exits: 0 }; });
    exits.forEach(e => {
        const k = `${e.year}-${e.month}`;
        if (m[k]) m[k].exits = e.count;
        else m[k] = { month: e.month, year: e.year, hires: 0, exits: e.count };
    });
    return Object.values(m).sort((a,b) => a.year !== b.year ? a.year-b.year : a.month-b.month);
}
function rollupByMonth(rows = [], field) {
    const m = {};
    rows.forEach(r => {
        const key = r.month;
        m[key] = { month: key, [field]: (m[key]?.[field] || 0) + (parseInt(r[field]) || 0) };
    });
    return Object.values(m).sort((a,b) => a.month - b.month);
}
function getLeaveTypes(rows = []) {
    return [...new Set(rows.map(r => r.leave_type))];
}
function pivotLeaveByMonth(rows = []) {
    const m = {};
    rows.forEach(r => {
        if (!m[r.month]) m[r.month] = { month: r.month };
        m[r.month][r.leave_type] = (m[r.month][r.leave_type] || 0) + parseInt(r.days || 0);
    });
    return Object.values(m).sort((a,b) => a.month - b.month);
}

// Inline SVG CheckCircle — same as original (avoids import conflict)
const CheckCircle = ({ size, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" className={className}>
        <circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/>
    </svg>
);

export default ReportsPage;
